import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
} from 'antd';
import { DataTable } from 'common/data-table';
import InfoCard from 'common/ui/info-card';
import { formatDateTime } from 'utils/formatDate';
import { useFetchApiList } from 'hooks/api';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiCall from 'apps/investcore/apiCalls/subscriptions';
import userApiCall from 'apps/investment/apiCalls/user/list';

// Subscribers/Transactions can each outgrow a single unpaginated page -
// every renewal adds a transaction row, every grant/purchase adds a
// subscriber - so both tables below are paged rather than rendering
// whatever the backend's default page_size (24, unrelated to this) hands
// back with no way to see more - same page/page_size + paging.count
// pattern as fund-order-reconciliation's Table pagination. Options has no
// such need (see its own useFetchApiList below) - a handful of pricing
// tiers per subscription, and GetOptions returns them unpaginated anyway.
const DETAIL_TABLE_PAGE_SIZE = 10;

// The Interest Registrations section on the main page is a preview, not a
// browsable list - just enough to see who's shown interest lately without
// the section eating the same vertical space as Subscribers/Transactions.
// Anything beyond this lives in the "View All" drawer, paged at
// DETAIL_TABLE_PAGE_SIZE like the other sections.
const INTEREST_REGISTRATIONS_PREVIEW_SIZE = 5;

// Transactions is a preview too, same reasoning as Interest Registrations
// above - every renewal adds a row forever, so this grows unbounded.
// Anything beyond this lives on the standalone /investment/subscription-
// transactions page (see "Xem tất cả" link below), which is also where
// filtering across every plan happens now.
const TRANSACTIONS_PREVIEW_SIZE = 5;

const paginationShowTotal = (total: number, range: [number, number]) =>
  `${range[0]}-${range[1]} of ${total}`;

const DURATION_LABELS: Record<number, string> = {
  1001: 'Monthly',
  1002: 'Yearly',
};

const STATUS_LABELS: Record<number, { label: string; color: string }> = {
  1001: { label: 'Draft', color: 'default' },
  1002: { label: 'Published', color: 'green' },
};

const DURATION_SELECT_OPTIONS = [
  { value: 1001, label: 'Monthly' },
  { value: 1002, label: 'Yearly' },
];

const STATUS_SELECT_OPTIONS = [
  { value: 1001, label: 'Draft' },
  { value: 1002, label: 'Published' },
];

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  voucher: 'Voucher',
};

const TRANSACTION_STATUS_LABELS: Record<number, { label: string; color: string }> = {
  1001: { label: 'Authorize', color: 'blue' },
  1002: { label: 'Capture', color: 'green' },
  1003: { label: 'Canceled', color: 'red' },
};

const OptionFormModal = ({
  open,
  onClose,
  subscriptionId,
  option,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
  option?: any;
  onSaved: () => void;
}) => {
  const [form] = Form.useForm();
  const isEdit = !!option;

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue(
      option
        ? {
            title: option.title,
            highlight: option.highlight,
            description: option.description,
            duration_type: option.duration_type,
            amount: option.amount,
            free_trial_days: option.free_trial_days,
            status: option.status,
            apple_product_id: option.apple_product_id,
            google_product_id: option.google_product_id,
          }
        : { free_trial_days: 0 }
    );
  }, [open, option]);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: any) =>
      isEdit
        ? ApiCall.editOption.queryFn({
            id: subscriptionId,
            optionId: option.id,
            payload,
          })
        : ApiCall.createOption.queryFn({ id: subscriptionId, payload }),
    onSuccess: () => {
      message.success(isEdit ? 'Option updated' : 'Option created');
      form.resetFields();
      onSaved();
      onClose();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to save option');
    },
  });

  return (
    <Modal
      title={isEdit ? 'Edit Pricing Option' : 'Add Pricing Option'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={values => mutate(values)}>
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item name="highlight" label="Highlight">
          <Input />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="duration_type"
          label="Duration"
          rules={[{ required: true }]}
        >
          <Select>
            {DURATION_SELECT_OPTIONS.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>

        <Form.Item name="free_trial_days" label="Free Trial Days">
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>

        <Form.Item
          name="apple_product_id"
          label="Apple Product ID"
          extra="Must match the product id configured in App Store Connect"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="google_product_id"
          label="Google Product ID"
          extra="Must match the product id configured in Play Console"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true }]}
          extra="Draft options are never shown or self-joinable in the app - only reachable via Grant Subscription below. That's the only way to make an option admin-grant-only/whitelist; Published options anyone can self-join."
        >
          <Select>
            {STATUS_SELECT_OPTIONS.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item className="mb-0">
          <Space className="w-full justify-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              Save
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Two independent signals, kept as separate columns instead of merged into
// one badge — they answer different ops questions:
// - Status: the raw DB flag (investcore/src/model/subscription.go
//   SubscriptionUserStatus). Only changes via an explicit Grant/Revoke action.
// - Is Active: computed the same way SubscriptionUser.IsActive() does
//   (status Active AND expired_at still in the future) — reflects reality
//   right now, since expiry is never written back to `status`.
// Together they let ops tell "naturally expired" (Status: Active, Is Active: No)
// apart from "explicitly revoked" (Status: Revoked, Is Active: No).
const RAW_STATUS_LABELS: Record<number, { label: string; color: string }> = {
  1001: { label: 'Revoked', color: 'default' },
  1002: { label: 'Active', color: 'green' },
};

// Same values/colors as iap-notifications/config.tsx's ENVIRONMENT_COLORS -
// kept in sync there since that's the only other place this field is shown.
const ENVIRONMENT_COLORS: Record<string, string> = {
  Sandbox: 'orange',
  Production: 'green',
};

const isUserSubscriptionActive = (row: any): boolean => {
  if (row?.status !== 1002 || !row?.expired_at || !row?.activated_at) {
    return false;
  }
  const now = Date.now();
  return (
    new Date(row.expired_at).getTime() > now &&
    new Date(row.activated_at).getTime() < now
  );
};

// A row's own apple_original_transaction_id/google_purchase_token (plain
// columns on subscription_users, not a preload) is the only reliable signal
// for "was this a real store purchase" - DeleteSubscriptionUser's backend
// guard checks payment_transactions directly, but this lets the UI show the
// same distinction (and gate the Delete button) without a round trip.
const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  apple: { label: 'Apple', color: 'default' },
  google: { label: 'Google', color: 'default' },
  manual: { label: 'Manual', color: 'blue' },
};

const getSubscriptionUserSource = (row: any): 'apple' | 'google' | 'manual' => {
  if (row?.apple_original_transaction_id) return 'apple';
  if (row?.google_purchase_token) return 'google';
  return 'manual';
};

const GrantUserModal = ({
  open,
  onClose,
  subscriptionId,
  options,
  onGranted,
}: {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
  options: any[];
  onGranted: () => void;
}) => {
  const [form] = Form.useForm();

  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fetchOnFocus, setFetchOnFocus] = useState(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => setDebouncedUserSearch(userSearch),
      300
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [userSearch]);

  const { data: userResponse, isLoading: isLoadingUsers } = useQuery({
    queryKey: [userApiCall.queryKey, { search: debouncedUserSearch }],
    queryFn: () => userApiCall.queryFn({ search: debouncedUserSearch }),
    enabled: open && (debouncedUserSearch.length > 0 || fetchOnFocus),
  });

  const userOptions = (userResponse?.data || []).map((item: any) => ({
    value: item.id,
    label: `${item.email || item.phone || item.id}${
      item.name ? ` - ${item.name}` : ''
    }`,
  }));

  const optionSelectOptions = (options || []).map((opt: any) => ({
    value: opt.id,
    label: `${opt.title} (${opt.amount_label})`,
  }));

  // Duration is a separate, explicit input rather than reusing the
  // selected option's free_trial_days - that field describes the
  // self-serve trial length (often 0 for a real paid option), not how
  // long a no-payment admin grant should last.
  const durationDays = Form.useWatch('duration_days', form);
  const expiresPreview =
    durationDays > 0
      ? formatDateTime(
          new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString()
        )
      : null;

  const { mutate, isPending } = useMutation({
    mutationFn: (values: { user_id: string; option_id: string; duration_days: number }) =>
      ApiCall.grantUser.queryFn({
        id: subscriptionId,
        userId: values.user_id,
        optionId: values.option_id,
        durationDays: values.duration_days,
      }),
    onSuccess: () => {
      message.success('Subscription granted');
      form.resetFields();
      onGranted();
      onClose();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to grant subscription');
    },
  });

  return (
    <Modal
      title="Grant Subscription"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={values => mutate(values)}>
        <Form.Item
          name="user_id"
          label="User"
          rules={[{ required: true, message: 'Please select a user' }]}
        >
          <Select
            showSearch
            loading={isLoadingUsers}
            options={userOptions}
            optionFilterProp="label"
            filterOption={false}
            placeholder="Search by email/phone/name"
            onSearch={val => setUserSearch(val)}
            onFocus={() => setFetchOnFocus(true)}
            notFoundContent={isLoadingUsers ? <Spin size="small" /> : null}
          />
        </Form.Item>

        <Form.Item
          name="option_id"
          label="Pricing Option"
          rules={[{ required: true, message: 'Please select an option' }]}
        >
          <Select
            options={optionSelectOptions}
            placeholder="Select a pricing option"
          />
        </Form.Item>

        <Form.Item
          name="duration_days"
          label="Duration (days)"
          rules={[{ required: true, message: 'Please enter a duration' }]}
          extra={
            expiresPreview
              ? `Expires at ${expiresPreview} - independent of the option's own trial/duration settings, and never auto-renews since no real payment backs it.`
              : "Independent of the option's own trial/duration settings, and never auto-renews since no real payment backs it."
          }
        >
          <InputNumber style={{ width: '100%' }} min={1} placeholder="e.g. 30" />
        </Form.Item>

        <Form.Item className="mb-0">
          <Space className="w-full justify-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              Grant
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// RegisterInterestModal is the admin-initiated counterpart of the app's
// pre-launch "notify me" button - support can register a user's Premium
// interest by user id, same no-payment/no-SubscriptionUser semantics as
// AppSubscriptionController.RegisterInterest. Reuses GrantUserModal's user
// search widget, just without the duration input.
const RegisterInterestModal = ({
  open,
  onClose,
  subscriptionId,
  options,
  onRegistered,
}: {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
  options: any[];
  onRegistered: () => void;
}) => {
  const [form] = Form.useForm();

  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fetchOnFocus, setFetchOnFocus] = useState(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => setDebouncedUserSearch(userSearch),
      300
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [userSearch]);

  const { data: userResponse, isLoading: isLoadingUsers } = useQuery({
    queryKey: [userApiCall.queryKey, { search: debouncedUserSearch }],
    queryFn: () => userApiCall.queryFn({ search: debouncedUserSearch }),
    enabled: open && (debouncedUserSearch.length > 0 || fetchOnFocus),
  });

  const userOptions = (userResponse?.data || []).map((item: any) => ({
    value: item.id,
    label: `${item.email || item.phone || item.id}${
      item.name ? ` - ${item.name}` : ''
    }`,
  }));

  const optionSelectOptions = (options || []).map((opt: any) => ({
    value: opt.id,
    label: `${opt.title} (${opt.amount_label})`,
  }));

  const { mutate, isPending } = useMutation({
    mutationFn: (values: { user_id: string; option_id: string }) =>
      ApiCall.registerInterest.queryFn({
        id: subscriptionId,
        userId: values.user_id,
        optionId: values.option_id,
      }),
    onSuccess: () => {
      message.success('Interest registered');
      form.resetFields();
      onRegistered();
      onClose();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to register interest');
    },
  });

  return (
    <Modal
      title="Register Interest"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={values => mutate(values)}>
        <Form.Item
          name="user_id"
          label="User"
          rules={[{ required: true, message: 'Please select a user' }]}
        >
          <Select
            showSearch
            loading={isLoadingUsers}
            options={userOptions}
            optionFilterProp="label"
            filterOption={false}
            placeholder="Search by email/phone/name"
            onSearch={val => setUserSearch(val)}
            onFocus={() => setFetchOnFocus(true)}
            notFoundContent={isLoadingUsers ? <Spin size="small" /> : null}
          />
        </Form.Item>

        <Form.Item
          name="option_id"
          label="Pricing Option"
          rules={[{ required: true, message: 'Please select an option' }]}
        >
          <Select
            options={optionSelectOptions}
            placeholder="Select a pricing option"
          />
        </Form.Item>

        <Form.Item className="mb-0">
          <Space className="w-full justify-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              Register
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ItemDetail = ({ item, apiCall }: { item: any; apiCall: any }) => {
  const {
    id,
    title,
    slug,
    description,
    excerpt,
    status,
    type,
    created_at,
    updated_at,
  } = item || {};

  const [usersPage, setUsersPage] = useState(1);
  const [interestRegistrationsDrawerOpen, setInterestRegistrationsDrawerOpen] =
    useState(false);
  const [interestRegistrationsDrawerPage, setInterestRegistrationsDrawerPage] =
    useState(1);

  const {
    data: users,
    load: loadUsers,
    refresh: refreshUsers,
    paging: usersPaging,
  } = useFetchApiList(ApiCall.users, { resourceName: 'data' });

  // Options has no realistic pagination need - GetOptions returns the
  // subscription's full SubscriptionOptions relation unpaginated (a
  // handful of pricing tiers per subscription, not open-ended like
  // subscribers/transactions), and the backend doesn't send a `paging`
  // object for it at all.
  const {
    data: options,
    load: loadOptions,
    refresh: refreshOptions,
  } = useFetchApiList(ApiCall.options, { resourceName: 'data' });

  const {
    data: transactions,
    load: loadTransactions,
    paging: transactionsPaging,
  } = useFetchApiList(ApiCall.transactions, { resourceName: 'data' });

  const {
    data: interestRegistrations,
    load: loadInterestRegistrations,
    refresh: refreshInterestRegistrations,
    paging: interestRegistrationsPaging,
  } = useFetchApiList(ApiCall.interestRegistrations, { resourceName: 'data' });

  // Independent fetch/paging state for the "View All" drawer - opening or
  // paging through it must not disturb the top-5 preview above.
  const {
    data: interestRegistrationsDrawerData,
    load: loadInterestRegistrationsDrawer,
    refresh: refreshInterestRegistrationsDrawer,
    paging: interestRegistrationsDrawerPaging,
  } = useFetchApiList(ApiCall.interestRegistrations, { resourceName: 'data' });

  useEffect(() => {
    if (id) {
      loadUsers({ id, page: usersPage, page_size: DETAIL_TABLE_PAGE_SIZE });
    }
  }, [id, usersPage]);

  useEffect(() => {
    if (id) {
      loadOptions({ id });
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadTransactions({ id, page: 1, page_size: TRANSACTIONS_PREVIEW_SIZE });
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadInterestRegistrations({
        id,
        page: 1,
        page_size: INTEREST_REGISTRATIONS_PREVIEW_SIZE,
      });
    }
  }, [id]);

  useEffect(() => {
    if (id && interestRegistrationsDrawerOpen) {
      loadInterestRegistrationsDrawer({
        id,
        page: interestRegistrationsDrawerPage,
        page_size: DETAIL_TABLE_PAGE_SIZE,
      });
    }
  }, [id, interestRegistrationsDrawerOpen, interestRegistrationsDrawerPage]);

  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<any>(undefined);

  const { mutate: deleteOption, isPending: isDeletingOption } = useMutation({
    mutationFn: (params: { id: string; optionId: string }) =>
      ApiCall.deleteOption.queryFn(params),
    onSuccess: () => {
      message.success('Option deleted');
      refreshOptions();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to delete option');
    },
  });

  const openCreateOption = () => {
    setEditingOption(undefined);
    setOptionModalOpen(true);
  };

  const openEditOption = (option: any) => {
    setEditingOption(option);
    setOptionModalOpen(true);
  };

  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [registerInterestModalOpen, setRegisterInterestModalOpen] = useState(false);

  const { mutate: revokeUser, isPending: isRevokingUser } = useMutation({
    mutationFn: (params: { id: string; userId: string }) =>
      ApiCall.revokeUser.queryFn(params),
    onSuccess: () => {
      message.success('Subscription revoked');
      refreshUsers();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to revoke subscription');
    },
  });

  // Hard-deletes the subscription_users row - the backend rejects this
  // (ErrSubscriptionUserHasTransactions) only when one of its transactions
  // is confirmed Production (deny-list, synced with the notification/
  // transaction delete actions below - see SubscriptionService.
  // DeleteSubscriptionUser). A row the server can't positively identify as
  // Production (empty/unknown Environment) is still deletable - the
  // Popconfirm below is the safeguard for that ambiguity, not this check.
  const { mutate: deleteUser, isPending: isDeletingUser } = useMutation({
    mutationFn: (params: { id: string; userId: string }) =>
      ApiCall.deleteUser.queryFn(params),
    onSuccess: () => {
      message.success('Subscriber deleted');
      refreshUsers();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to delete subscriber');
    },
  });

  // Backend blocks this only for rows confirmed Production (deny-list, not
  // an allow-list requiring Sandbox - see SubscriptionService.DeleteTransaction)
  // - this Popconfirm's own display of the row's Environment is the
  // safeguard for anything the server can't positively classify itself.
  const { mutate: deleteTransaction, isPending: isDeletingTransaction } = useMutation({
    mutationFn: (params: { id: string; transactionId: string }) =>
      ApiCall.deleteTransaction.queryFn(params),
    onSuccess: () => {
      message.success('Transaction deleted');
      loadTransactions({ id, page: 1, page_size: TRANSACTIONS_PREVIEW_SIZE });
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to delete transaction');
    },
  });

  // notified_at is set by hand from here once support has manually sent the
  // launch notification - nothing in this app sends it, this is only a record.
  const { mutate: updateInterestRegistrationNotified, isPending: isUpdatingInterestRegistration } =
    useMutation({
      mutationFn: (params: { id: string; registrationId: string; payload: { notified_at: string | null } }) =>
        ApiCall.updateInterestRegistration.queryFn(params),
      onSuccess: () => {
        refreshInterestRegistrations();
        refreshInterestRegistrationsDrawer();
      },
      onError: (err: any) => {
        message.error(err?.message || 'Failed to update notified status');
      },
    });

  const userColumns = [
    {
      accessorKey: 'user.email',
      header: 'Email',
      cell: ({ row }) => row.original?.user?.email ?? '-',
    },
    {
      accessorKey: 'user.name',
      header: 'Name',
      cell: ({ row }) => row.original?.user?.name ?? '-',
    },
    {
      accessorKey: 'user.phone',
      header: 'Phone',
      cell: ({ row }) => row.original?.user?.phone ?? '-',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const meta = RAW_STATUS_LABELS[getValue()];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : getValue();
      },
    },
    {
      accessorKey: 'is_active',
      header: 'Is Active',
      cell: ({ row }) =>
        isUserSubscriptionActive(row.original) ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => {
        const meta = SOURCE_LABELS[getSubscriptionUserSource(row.original)];
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      accessorKey: 'environment',
      header: 'Environment',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return '-';
        return <Tag color={ENVIRONMENT_COLORS[value] ?? 'default'}>{value}</Tag>;
      },
    },
    {
      accessorKey: 'activated_at',
      header: 'Activated At',
      cell: ({ getValue }) => formatDateTime(getValue()),
    },
    {
      accessorKey: 'expired_at',
      header: 'Expired At',
      cell: ({ getValue }) => formatDateTime(getValue()),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        // Mirrors the backend's deny-list policy (SubscriptionService.
        // DeleteSubscriptionUser): only a subscriber backed by a confirmed
        // Production transaction is protected. Sandbox and unknown/empty
        // Environment are deletable - this Popconfirm's own display of the
        // row's Environment is the safeguard for that ambiguity, not a
        // disabled button.
        const isProduction = row.original.environment === 'Production';
        return (
          <Space>
            <Link
              to={`/investment/iap-notifications?subscription_user_id=${row.original.id}`}
            >
              <Button size="small">IAP Notifications</Button>
            </Link>
            <Popconfirm
              title="Revoke this user's subscription?"
              okText="Revoke"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() =>
                revokeUser({ id, userId: row.original.user_id })
              }
            >
              <Button size="small" danger loading={isRevokingUser}>
                Revoke
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Delete this subscriber?"
              description={
                <>
                  Environment: <b>{row.original.environment || 'unknown'}</b>.
                  Deletes the subscriber, its transactions, and any linked
                  notifications. This cannot be undone. Production rows are
                  rejected by the server regardless of this confirmation.
                </>
              }
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() =>
                deleteUser({ id, userId: row.original.user_id })
              }
              disabled={isProduction}
            >
              <Button
                size="small"
                danger
                loading={isDeletingUser}
                disabled={isProduction}
                title={
                  isProduction
                    ? 'Production subscriber - cannot be deleted'
                    : undefined
                }
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const optionColumns = [
    { accessorKey: 'title', header: 'Title' },
    { accessorKey: 'highlight', header: 'Highlight' },
    {
      accessorKey: 'duration_type',
      header: 'Duration',
      cell: ({ getValue }) => DURATION_LABELS[getValue()] ?? getValue(),
    },
    { accessorKey: 'amount_label', header: 'Price' },
    { accessorKey: 'free_trial_days', header: 'Free Trial Days' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const meta = STATUS_LABELS[getValue()];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : getValue();
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Space>
          <Button size="small" onClick={() => openEditOption(row.original)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this option?"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() =>
              deleteOption({ id, optionId: row.original.id })
            }
          >
            <Button size="small" danger loading={isDeletingOption}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const transactionColumns = [
    {
      accessorKey: 'user.email',
      header: 'Email',
      cell: ({ row }) => row.original?.user?.email ?? '-',
    },
    {
      accessorKey: 'method',
      header: 'Method',
      cell: ({ getValue }) => PAYMENT_METHOD_LABELS[getValue()] ?? getValue(),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
    },
    {
      // amount_charged is the real amount the store collected (0 during a
      // free trial) - amount above is always the plan's nominal list price,
      // regardless of trial status. null means not yet backfilled - see
      // backfill_transaction_trial_amount_task.go.
      accessorKey: 'amount_charged',
      header: 'Amount Charged',
      cell: ({ getValue }) => {
        const value = getValue();
        return value == null ? '-' : value;
      },
    },
    {
      accessorKey: 'is_trial',
      header: 'Trial',
      cell: ({ getValue }) =>
        getValue() ? <Tag color="blue">Trial</Tag> : '-',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const meta = TRANSACTION_STATUS_LABELS[getValue()];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : getValue();
      },
    },
    {
      // PaymentTransaction has its own environment column, set independently
      // from the SubscriptionUser row it's linked to (see payment_service.go
      // Process()) - empty for Voucher, which has no store environment.
      accessorKey: 'environment',
      header: 'Environment',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return '-';
        return <Tag color={ENVIRONMENT_COLORS[value] ?? 'default'}>{value}</Tag>;
      },
    },
    { accessorKey: 'tx_id', header: 'TxID' },
    { accessorKey: 'tx_product_id', header: 'Product ID' },
    {
      accessorKey: 'subscription_user.apple_original_transaction_id',
      header: 'Apple Original Tx ID',
      cell: ({ row }) =>
        row.original?.subscription_user?.apple_original_transaction_id || '-',
    },
    {
      accessorKey: 'subscription_user.google_purchase_token',
      header: 'Google Purchase Token',
      cell: ({ row }) =>
        row.original?.subscription_user?.google_purchase_token || '-',
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ getValue }) => formatDateTime(getValue()),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Popconfirm
          title="Delete this transaction?"
          description={
            <>
              Environment: <b>{row.original.environment || 'unknown'}</b>.
              This cannot be undone. Production rows are rejected by the
              server regardless of this confirmation.
            </>
          }
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() =>
            deleteTransaction({ id, transactionId: row.original.id })
          }
        >
          <Button size="small" danger loading={isDeletingTransaction}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const interestRegistrationColumns = [
    {
      accessorKey: 'user.email',
      header: 'Email',
      cell: ({ row }) => row.original?.user?.email ?? '-',
    },
    {
      accessorKey: 'subscription_option.title',
      header: 'Plan',
      cell: ({ row }) => row.original?.subscription_option?.title ?? '-',
    },
    {
      accessorKey: 'created_at',
      header: 'Registered At',
      cell: ({ getValue }) => formatDateTime(getValue()),
    },
    {
      accessorKey: 'notified_at',
      header: 'Notified',
      cell: ({ row }) => (
        <Space>
          <Switch
            size="small"
            checked={!!row.original.notified_at}
            loading={isUpdatingInterestRegistration}
            onChange={(checked) =>
              updateInterestRegistrationNotified({
                id,
                registrationId: row.original.id,
                payload: { notified_at: checked ? new Date().toISOString() : null },
              })
            }
          />
          {row.original.notified_at ? formatDateTime(row.original.notified_at) : '-'}
        </Space>
      ),
    },
  ];

  const statusMeta = STATUS_LABELS[status];

  const interestCount = interestRegistrationsPaging?.count;
  const subscribersCount = usersPaging?.count;
  const transactionsCount = transactionsPaging?.count;

  const conversionCaption = (num?: number, denom?: number, from?: string) =>
    typeof num === 'number' && typeof denom === 'number' && denom > 0
      ? `${((num / denom) * 100).toFixed(1)}% of ${from}`
      : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard label="Title" value={title || '-'} />
        <InfoCard label="Slug" value={slug || '-'} />
        <InfoCard
          label="Status"
          value={
            statusMeta ? (
              <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
            ) : (
              status
            )
          }
        />
        <InfoCard label="Type" value={type ?? '-'} />
        <InfoCard
          className="md:col-span-2"
          label="Excerpt"
          value={excerpt || '-'}
        />
        <InfoCard
          className="md:col-span-3"
          label="Description"
          value={description || '-'}
        />
        <InfoCard label="Created At" value={formatDateTime(created_at)} />
        <InfoCard label="Updated At" value={formatDateTime(updated_at)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard
          label="Interest Registrations"
          value={
            <span className="text-2xl font-semibold">
              {interestCount ?? '-'}
            </span>
          }
        />
        <InfoCard
          label="Subscribers"
          value={
            <div>
              <span className="text-2xl font-semibold">
                {subscribersCount ?? '-'}
              </span>
              {conversionCaption(subscribersCount, interestCount, 'interest') && (
                <div className="text-xs font-normal text-gray-500 mt-1">
                  {conversionCaption(subscribersCount, interestCount, 'interest')}
                </div>
              )}
            </div>
          }
        />
        <InfoCard
          label="Transactions"
          value={
            <div>
              <span className="text-2xl font-semibold">
                {transactionsCount ?? '-'}
              </span>
              {conversionCaption(
                transactionsCount,
                subscribersCount,
                'subscribers'
              ) && (
                <div className="text-xs font-normal text-gray-500 mt-1">
                  {conversionCaption(
                    transactionsCount,
                    subscribersCount,
                    'subscribers'
                  )}
                </div>
              )}
            </div>
          }
        />
      </div>

      <div className="flex items-center justify-between pt-2 border-b">
        <h3 className="text-base font-medium text-gray-800">
          Pricing Options
        </h3>
        <Button type="primary" size="small" onClick={openCreateOption}>
          Add Option
        </Button>
      </div>
      <DataTable
        noViewOptions
        data={options || []}
        columns={optionColumns}
      />

      <div className="flex items-center justify-between pt-2 border-b">
        <h3 className="text-base font-medium text-gray-800">
          Interest Registrations
          {interestCount != null ? ` (${interestCount})` : ''}
        </h3>
        <Space>
          {interestCount > INTEREST_REGISTRATIONS_PREVIEW_SIZE && (
            <Button
              size="small"
              onClick={() => {
                setInterestRegistrationsDrawerPage(1);
                setInterestRegistrationsDrawerOpen(true);
              }}
            >
              View All
            </Button>
          )}
          <Button
            size="small"
            onClick={() => setRegisterInterestModalOpen(true)}
            disabled={!options?.length}
          >
            Register Interest
          </Button>
        </Space>
      </div>
      <DataTable
        noViewOptions
        data={interestRegistrations || []}
        columns={interestRegistrationColumns}
      />

      <div className="flex items-center justify-between pt-2 border-b">
        <h3 className="text-base font-medium text-gray-800">
          Subscribers{subscribersCount != null ? ` (${subscribersCount})` : ''}
        </h3>
        <Button
          type="primary"
          size="small"
          onClick={() => setGrantModalOpen(true)}
          disabled={!options?.length}
        >
          Grant Subscription
        </Button>
      </div>
      <DataTable
        noViewOptions
        data={users || []}
        columns={userColumns}
        minRows={DETAIL_TABLE_PAGE_SIZE}
      />
      {usersPaging?.count > DETAIL_TABLE_PAGE_SIZE && (
        <Pagination
          className="flex justify-end pt-2"
          size="small"
          current={usersPage}
          pageSize={DETAIL_TABLE_PAGE_SIZE}
          total={usersPaging.count}
          showSizeChanger={false}
          showTotal={paginationShowTotal}
          onChange={setUsersPage}
        />
      )}

      <div className="flex items-center justify-between pt-2 border-b">
        <h3 className="text-base font-medium text-gray-800">
          Transactions{transactionsCount != null ? ` (${transactionsCount})` : ''}
        </h3>
        {transactionsCount > TRANSACTIONS_PREVIEW_SIZE && (
          <Link to={`/investment/subscription-transactions?subscription_id=${id}`}>
            <Button size="small">Xem tất cả</Button>
          </Link>
        )}
      </div>
      <DataTable
        noViewOptions
        data={transactions || []}
        columns={transactionColumns}
        minRows={TRANSACTIONS_PREVIEW_SIZE}
      />

      <OptionFormModal
        open={optionModalOpen}
        onClose={() => setOptionModalOpen(false)}
        subscriptionId={id}
        option={editingOption}
        onSaved={refreshOptions}
      />

      <GrantUserModal
        open={grantModalOpen}
        onClose={() => setGrantModalOpen(false)}
        subscriptionId={id}
        options={options}
        onGranted={refreshUsers}
      />

      <RegisterInterestModal
        open={registerInterestModalOpen}
        onClose={() => setRegisterInterestModalOpen(false)}
        subscriptionId={id}
        options={options}
        onRegistered={() => {
          refreshInterestRegistrations();
          if (interestRegistrationsDrawerOpen) {
            refreshInterestRegistrationsDrawer();
          }
        }}
      />

      <Drawer
        title={`Interest Registrations${
          interestRegistrationsDrawerPaging?.count != null
            ? ` (${interestRegistrationsDrawerPaging.count})`
            : ''
        }`}
        open={interestRegistrationsDrawerOpen}
        onClose={() => setInterestRegistrationsDrawerOpen(false)}
        width={720}
      >
        <DataTable
          noViewOptions
          data={interestRegistrationsDrawerData || []}
          columns={interestRegistrationColumns}
          minRows={DETAIL_TABLE_PAGE_SIZE}
        />
        {interestRegistrationsDrawerPaging?.count > DETAIL_TABLE_PAGE_SIZE && (
          <Pagination
            className="flex justify-end pt-2"
            size="small"
            current={interestRegistrationsDrawerPage}
            pageSize={DETAIL_TABLE_PAGE_SIZE}
            total={interestRegistrationsDrawerPaging.count}
            showSizeChanger={false}
            showTotal={paginationShowTotal}
            onChange={setInterestRegistrationsDrawerPage}
          />
        )}
      </Drawer>
    </div>
  );
};

export default ItemDetail;
